import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { Bug, X } from 'lucide-react';
import { reportProblem } from '../api/candidate';

/** Adresse de contact publique de Permio : le repli quand un signalement ne part pas. */
const SUPPORT_EMAIL = 'contact@permio.fr';
const MIN_LENGTH = 5;
const MAX_LENGTH = 4000;

function serverMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown } | undefined)
      ?.message;
    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message.length > 0) return message.join(' ');
  }
  return fallback;
}

/**
 * Signalement d'un problème au service technique Permio, depuis les messages de l'élève.
 * Ce n'est pas une conversation avec l'auto-école : le texte part par e-mail à l'éditeur de
 * l'application, et la réponse arrive par e-mail.
 */
export default function BugReportDialog({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState('');
  const text = message.trim();
  const send = useMutation({
    mutationFn: () =>
      // L'élève, son école et son navigateur sont ajoutés par le serveur ; seule la page part d'ici.
      reportProblem({ message: text, page: window.location.pathname }),
  });

  // À l'ouverture : la saisie. Une fois envoyé, la saisie disparaît : le focus passe au bouton de
  // retour, pour que le clavier (Échap, Entrée) continue d'agir sur la fenêtre.
  useEffect(() => {
    const target = send.isSuccess
      ? panelRef.current?.querySelector<HTMLElement>('[data-done]')
      : panelRef.current?.querySelector('textarea');
    target?.focus();
  }, [send.isSuccess]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    // Le focus reste dans la fenêtre tant qu'elle est ouverte.
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea, a[href]',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;
    if (e.shiftKey && (current === first || current === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && current === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="messaging-dialog messaging-report"
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2 id={titleId}>
            {send.isSuccess ? 'Signalement envoyé' : 'Signaler un problème'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="messaging-close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {send.isSuccess ? (
          <>
            <p role="status" className="mb-4">
              Merci. Votre message est parti au service technique Permio.{' '}
              {send.data.replyTo
                ? `Nous vous répondrons par e-mail à ${send.data.replyTo}.`
                : 'Nous vous répondrons par e-mail.'}
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                data-done
                onClick={onClose}
                className="messaging-button"
              >
                Retour aux messages
              </button>
            </div>
          </>
        ) : (
          <form
            className="messaging-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.length >= MIN_LENGTH && !send.isPending) send.mutate();
            }}
          >
            <span className="messaging-report-mark justify-self-start">
              <Bug size={12} aria-hidden="true" />
              Service technique Permio
            </span>
            <p>
              Un souci avec l’application ? Écrivez au service technique
              Permio, qui l’édite. Pour une question sur votre formation,
              écrivez plutôt à votre auto-école.
            </p>
            <label>
              Que s’est-il passé ?
              <textarea
                rows={5}
                required
                minLength={MIN_LENGTH}
                maxLength={MAX_LENGTH}
                placeholder="Ce que vous faisiez, ce qui s’est passé, ce que vous attendiez."
                value={message}
                readOnly={send.isPending}
                onChange={(e) => {
                  if (send.isError) send.reset();
                  setMessage(e.target.value);
                }}
                className="messaging-field"
              />
            </label>
            <p className="messaging-hint">
              Votre message part par e-mail au service technique Permio, avec
              votre nom, votre adresse e-mail, votre auto-école, la page
              ouverte et votre navigateur. Rien d’autre n’est joint.
            </p>
            {send.isError && (
              <p role="alert" className="messaging-alert">
                {serverMessage(
                  send.error,
                  `Le signalement n’a pas pu être envoyé. Écrivez-nous à ${SUPPORT_EMAIL}.`,
                )}{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>
                  Ouvrir un e-mail
                </a>
              </p>
            )}
            <div className="messaging-form-actions">
              <button
                type="button"
                onClick={onClose}
                disabled={send.isPending}
                className="messaging-button messaging-button--secondary"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={text.length < MIN_LENGTH || send.isPending}
                className="messaging-button"
              >
                {send.isPending ? 'Envoi…' : 'Envoyer le signalement'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
